import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "name",
    "shortDescription",
    "description",
    "category",
    "state",
    "city",
    "address",
    "zipCode",
    "phone",
    "email",
    "whatsapp",
    "website",
    "instagram",
    "facebook",
    "badge",
    "previewBadge",
    "previewBadges",
    "featured",
    "logo",
    "cover",
    "previewName",
    "previewCategory",
    "previewDescription",
    "previewAddress",
    "previewPhone",
    "previewEmail",
    "previewInstagram",
    "previewFacebook",
    "previewWhatsapp",
    "previewWebsite",
    "previewCall",
    "previewInstagramCta",
    "previewEmailCta",
    "previewFacebookCta",
    "previewFeatured",
    "previewLogo",
    "previewLogoFallback",
    "previewCover",
  ]

  static values = { whatsappMessage: String }

  connect() {
    this.logoObjectUrl = null
    this.coverObjectUrl = null
    this.refresh()
  }

  disconnect() {
    this.revokeLogo()
    this.revokeCover()
  }

  refresh() {
    this.updateText()
    this.updateFeatured()
    this.updateBadges()
    this.updateLogoFromExisting()
    this.updateCoverFromExisting()
  }

  updateBadges() {
    if (!this.hasPreviewBadgesTarget) return
    const selected = new Set(this.badgeTargets.filter(input => input.checked).map(input => input.value))
    let visible = false
    this.previewBadgeTargets.forEach(item => {
      const show = selected.has(item.dataset.badgeId)
      item.classList.toggle("d-none", !show)
      visible ||= show
    })
    this.previewBadgesTarget.classList.toggle("d-none", !visible)
  }

  updateText() {
    const name = this.nameTarget?.value?.trim() || "Nome do negócio"
    if (this.hasPreviewNameTarget) this.previewNameTarget.textContent = name

    if (this.hasPreviewLogoFallbackTarget) {
      this.previewLogoFallbackTarget.textContent = name.slice(0, 2).toUpperCase()
    }

    if (this.hasCategoryTarget && this.hasPreviewCategoryTarget) {
      const selected = this.categoryTarget.selectedOptions[0]
      this.previewCategoryTarget.textContent = selected?.text?.trim() || "Categoria"
    }

    if (this.hasPreviewDescriptionTarget) {
      const html = this.descriptionTarget?.value?.trim()
      const short = this.shortDescriptionTarget?.value?.trim()
      if (html) {
        this.previewDescriptionTarget.replaceChildren(...this.sanitizePreviewHtml(html))
      } else {
        this.previewDescriptionTarget.textContent =
          short || "A descrição do negócio aparecerá aqui."
      }
    }

    if (this.hasPreviewAddressTarget) {
      const parts = [
        this.addressTarget?.value?.trim(),
        this.cityTarget?.value?.trim(),
        this.stateTarget?.selectedOptions?.[0]?.text?.match(/\(([A-Z]{2})\)/)?.[1] ||
          this.stateTarget?.selectedOptions?.[0]?.text?.trim(),
        this.zipCodeTarget?.value?.trim(),
      ].filter(Boolean)
      this.previewAddressTarget.textContent = parts.join(" · ") || "Endereço"
      this.previewAddressTarget.closest(".guia-biz__meta-row")?.classList.toggle("d-none", parts.length === 0)
    }

    this.toggleMetaRow("previewPhone", this.phoneTarget?.value?.trim())
    this.toggleMetaRow("previewEmail", this.emailTarget?.value?.trim())
    this.toggleMetaRow("previewInstagram", this.instagramTarget?.value?.trim())
    this.toggleMetaRow("previewFacebook", this.facebookTarget?.value?.trim())

    this.updateCtas()
  }

  // Same queue as Guia::ContactCtasComponent: WhatsApp and site first, then phone,
  // Instagram, email and Facebook, capped at two so the preview matches the public page.
  updateCtas() {
    const channels = [
      { key: "whatsapp", target: "previewWhatsapp" },
      { key: "website", target: "previewWebsite" },
      { key: "call", target: "previewCall" },
      { key: "instagram", target: "previewInstagramCta" },
      { key: "email", target: "previewEmailCta" },
      { key: "facebook", target: "previewFacebookCta" },
    ]

    channels.forEach((channel) => {
      const el = this.previewTarget(channel.target)
      if (!el) return

      el.classList.add("d-none")
    })

    // Same gate as Guia::ContactCtasComponent: a filled field that cannot become a
    // real href (phone with no digits, junk URL) does not occupy a CTA slot.
    channels
      .map((channel) => ({ ...channel, href: this.ctaHref(channel.key) }))
      .filter((channel) => channel.href)
      .slice(0, 2)
      .forEach((channel, index) => {
        const el = this.previewTarget(channel.target)
        if (!el) return

        el.className = this.ctaClassName(channel.key, index)
        el.setAttribute("href", channel.href)
      })
  }

  previewTarget(name) {
    const flag = `has${name.charAt(0).toUpperCase()}${name.slice(1)}Target`
    return this[flag] ? this[`${name}Target`] : null
  }

  ctaClassName(key, index) {
    if (key === "whatsapp") return "guia-btn-wa"
    if (index === 0) return "guia-btn-primary"
    return "guia-a guia-a-web guia-biz__site text-center"
  }

  ctaHref(key) {
    switch (key) {
      case "whatsapp": {
        const digits = (this.whatsappTarget?.value || "").replace(/\D/g, "")
        if (!digits) return null
        return `https://wa.me/${digits}?text=${encodeURIComponent(this.whatsappMessageValue)}`
      }
      case "website":
        return this.externalHref(this.websiteTarget?.value)
      case "call":
        return this.telHref(this.phoneTarget?.value)
      case "instagram":
        return this.externalHref(this.instagramTarget?.value)
      case "email": {
        const email = this.emailTarget?.value?.trim()
        return email ? `mailto:${email}` : null
      }
      case "facebook":
        return this.externalHref(this.facebookTarget?.value)
      default:
        return null
    }
  }

  // Mirrors ApplicationHelper#safe_external_url: scheme defaults to https, then
  // only http(s) with a host survive. Spaces and javascript: drop out.
  externalHref(value) {
    let url = value?.trim()
    if (!url) return null
    if (!/^[a-z][a-z0-9+\-.]*:/i.test(url)) url = `https://${url}`

    try {
      const parsed = new URL(url)
      if (![ "http:", "https:" ].includes(parsed.protocol) || !parsed.hostname) return null

      return parsed.toString()
    } catch {
      return null
    }
  }

  telHref(value) {
    const raw = (value || "").trim()
    if (!raw) return null

    const match = raw.match(/^(.+?)(?:\s*(?:ext\.?|extension|ramal|x)\.?\s*)(\d+)$/i)
    const main = match ? match[1] : raw
    const digits = main.replace(/\D/g, "")
    if (!digits) return null

    let href = `tel:${raw.startsWith("+") ? "+" : ""}${digits}`
    if (match?.[2]) href += `;ext=${match[2]}`
    return href
  }

  sanitizePreviewHtml(html) {
    const template = document.createElement("template")
    template.innerHTML = html

    template.content.querySelectorAll("script, iframe, object, embed").forEach((el) => el.remove())
    template.content.querySelectorAll("*").forEach((el) => {
      [...el.attributes].forEach((attr) => {
        const name = attr.name.toLowerCase()
        const value = attr.value || ""
        if (name.startsWith("on") || ((name === "href" || name === "src") && /^\s*javascript:/i.test(value))) {
          el.removeAttribute(attr.name)
        }
      })
    })

    return [...template.content.childNodes]
  }

  toggleMetaRow(targetName, value) {
    const hasTarget = this[`has${targetName.charAt(0).toUpperCase()}${targetName.slice(1)}Target`]
    if (!hasTarget) return
    const el = this[`${targetName}Target`]
    el.textContent = value || ""
    el.closest(".guia-biz__meta-row")?.classList.toggle("d-none", !value)
  }

  updateFeatured() {
    if (!this.hasPreviewFeaturedTarget) return
    const featured = this.hasFeaturedTarget && this.featuredTarget.checked
    this.previewFeaturedTarget.classList.toggle("d-none", !featured)
  }

  logoChanged() {
    const file = this.logoTarget?.files?.[0]
    if (!file) return

    this.revokeLogo()
    this.logoObjectUrl = URL.createObjectURL(file)
    this.showLogo(this.logoObjectUrl)
  }

  coverChanged() {
    const file = this.coverTarget?.files?.[0]
    if (!file) return

    this.revokeCover()
    this.coverObjectUrl = URL.createObjectURL(file)
    this.showCover(this.coverObjectUrl)
  }

  updateLogoFromExisting() {
    if (this.logoObjectUrl) return
    const current = this.logoTarget?.dataset?.previewUrl
    if (current) this.showLogo(current)
  }

  updateCoverFromExisting() {
    if (this.coverObjectUrl) return
    const current = this.coverTarget?.dataset?.previewUrl
    if (current) this.showCover(current)
  }

  showLogo(url) {
    if (!this.hasPreviewLogoTarget || !this.hasPreviewLogoFallbackTarget) return
    this.previewLogoTarget.src = url
    this.previewLogoTarget.classList.remove("d-none")
    this.previewLogoFallbackTarget.classList.add("d-none")
  }

  showCover(url) {
    if (!this.hasPreviewCoverTarget) return
    this.previewCoverTarget.style.backgroundImage = `url(${url})`
  }

  revokeLogo() {
    if (this.logoObjectUrl) {
      URL.revokeObjectURL(this.logoObjectUrl)
      this.logoObjectUrl = null
    }
  }

  revokeCover() {
    if (this.coverObjectUrl) {
      URL.revokeObjectURL(this.coverObjectUrl)
      this.coverObjectUrl = null
    }
  }
}
